import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { hasPermission } from '../../utils/permissionUtils';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Box,
  Alert,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Divider,
  useTheme,
  alpha
} from "@mui/material";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ListPronoun from './ListPronoun';

export default function AddPronoun() {
  const theme = useTheme();
  const [pronounValue, setPronounValue] = useState("");
  const [inputReplaceNoun, setInputReplaceNoun] = useState("");
  const [pronoun, setPronoun] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [isSaving, setIsSaving] = useState(false);
  const { chatbotId: paramsChatbotId } = useParams();

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    document.title = "Add Pronoun | KlonIT Web Application";
  }, []);

  // Permission Check
  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('pronouns', 'view');
        const modifyPerm = await hasPermission('pronouns', 'add|edit');
        const deletePerm = await hasPermission('pronouns', 'delete');
        setCanView(viewPerm);
        setCanModify(modifyPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error("Error checking permissions:", error);
      } finally {
        setCheckingPermissions(false);
      }
    };
    checkPermissions();
  }, []);

  const handleAddPronoun = async () => {
    if (!pronounValue.trim()) {
      setMessage("Please enter a pronoun.");
      setMessageType("warning");
      return;
    }
    setIsSaving(true);
    setMessage("");

    try {
      const params = paramsChatbotId ? { chatbot_id: paramsChatbotId } : {};
      const response = await axiosInstance.post(`/add-pronoun`, {
        pronoun: pronounValue,
        replace_noun: inputReplaceNoun,
      }, { params });

      setPronoun([...pronoun, pronounValue]);
      setPronounValue("");
      setInputReplaceNoun("");
      setMessage(response.data.message || "Pronoun has been added successfully.");
      setMessageType("success");
    } catch (error) {
      const errMsg = error.response?.data?.message || "Error adding pronoun.";
      setMessage(errMsg);
      setMessageType("error");
    } finally {
      setIsSaving(false);
    }
  };

  if (checkingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1" color="textSecondary">Checking permissions...</Typography>
        </Stack>
      </Box>
    );
  }

  if (!canView) {
    return (
      <Box sx={{ mt: 6 }}>
        <Alert severity="error">
          <Typography variant="subtitle1" fontWeight={600}>Access Denied</Typography>
          <Typography variant="body2">
            You don't have permission to view pronoun settings. Please contact your administrator.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 3, mt: 10, mx: 'auto', maxWidth: '1200px' }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }} color="textPrimary">Add Pronoun</Typography>
      <Divider sx={{ mb: 3 }} />

      {message && (
        <Alert severity={messageType} sx={{ mb: 3 }} onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 4 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pronoun"
                placeholder="Enter pronoun"
                value={pronounValue}
                onChange={(e) => setPronounValue(e.target.value)}
                disabled={!canModify || isSaving}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Replace Pronoun"
                placeholder="Enter replacement"
                value={inputReplaceNoun}
                onChange={(e) => setInputReplaceNoun(e.target.value)}
                disabled={!canModify || isSaving}
              />
            </Grid>

            {canModify && (
              <Grid item xs={12} md={4} display="flex" alignItems="center">
                <Button
                  variant="contained"
                  startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutlineIcon />}
                  onClick={handleAddPronoun}
                  disabled={isSaving}
                  fullWidth
                  size="large"
                >
                  {isSaving ? "Adding..." : "Add Pronoun"}
                </Button>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <ListPronoun key={pronoun.length} chatbotId={paramsChatbotId} canDelete={canDelete} />

      <Alert severity="info" sx={{ mt: 4, borderRadius: 2, bgcolor: alpha(theme.palette.info.main, 0.08) }}>
        <Typography variant="body2" fontWeight={600}>Usage Notes</Typography>
        <Typography variant="body2">
          • Add new pronouns and their replacements for your chatbot’s responses.<br />
          • Example: Pronoun “He” replaced with “They”.
        </Typography>
      </Alert>
    </Paper>
  );
}
